import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Calculator, MessageCircle, Users, ShieldCheck, ArrowRight, TrendingUp } from 'lucide-react';
interface UpsellPageProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
}
export function UpsellPage({
  isOpen,
  onAccept,
  onDecline
}: UpsellPageProps) {
  return <AnimatePresence data-id="element-120">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] bg-cream overflow-y-auto" data-id="element-121">
          {/* Progress Bar */}
          <div className="sticky top-0 z-10 bg-white border-b border-chocolate/10 shadow-sm" data-id="element-122">
            <div className="max-w-3xl mx-auto px-4 py-3" data-id="element-123">
              <div className="flex items-center justify-between text-sm mb-2" data-id="element-124">
                <span className="font-bold text-chocolate" data-id="element-125">
                  Paso 2 de 3: Configura tu rentabilidad 📈
                </span>
                <span className="text-chocolate-light text-xs" data-id="element-126">
                  Didactia Digital
                </span>
              </div>
              <div className="w-full bg-cream-dark rounded-full h-2" data-id="element-127">
                <div className="bg-rose-600 h-2 rounded-full transition-all duration-500" style={{
              width: '66%'
            }} data-id="element-128" />
              </div>
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 py-10 md:py-16" data-id="element-129">
            {/* Reserved notice */}
            <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3 mb-8" data-id="element-130">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" data-id="element-131" />
              <p className="text-sm text-green-800 font-medium" data-id="element-132">
                ✅ Tus 8 ebooks ya están reservados. Esto es una mejora opcional
                para maximizar tus resultados.
              </p>
            </motion.div>

            {/* Headline */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-center mb-10" data-id="element-133">
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-chocolate leading-tight mb-6" data-id="element-134">
                ¡ESPERA! Tu pack de 8 ebooks está listo... pero te falta la{' '}
                <span className="text-rose-600" data-id="element-135">pieza clave</span> para ganar
                dinero real.
              </h1>
              <p className="text-lg md:text-xl text-chocolate-light leading-relaxed max-w-2xl mx-auto" data-id="element-136">
                Hacer postres deliciosos es el 50% del éxito. El otro 50% es{' '}
                <span className="font-bold text-chocolate" data-id="element-137">
                  saber cuánto cobrar y cómo cerrar ventas cada semana.
                </span>
              </p>
            </motion.div>

            {/* Divider */}
            <div className="flex items-center gap-4 my-10" data-id="element-138">
              <div className="flex-1 h-px bg-gold/30" data-id="element-139" />
              <TrendingUp className="w-6 h-6 text-gold" data-id="element-140" />
              <div className="flex-1 h-px bg-gold/30" data-id="element-141" />
            </div>

            {/* Value Section */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="mb-12" data-id="element-142">
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-chocolate text-center mb-3" data-id="element-143">
                KIT ACELERADOR DE VENTAS & COSTOS
              </h2>
              <p className="text-center text-chocolate-light mb-8" data-id="element-144">
                Las 3 herramientas que separan a las reposteras que "hornean
                bonito" de las que{' '}
                <span className="font-bold" data-id="element-145">facturan de verdad.</span>
              </p>

              <div className="space-y-4" data-id="element-146">
                {/* Item 1 */}
                <div className="bg-white rounded-2xl p-6 border border-chocolate/10 shadow-sm flex gap-5 items-start" data-id="element-147">
                  <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center shrink-0" data-id="element-148">
                    <Calculator className="w-6 h-6 text-rose-600" data-id="element-149" />
                  </div>
                  <div data-id="element-150">
                    <h3 className="font-bold text-chocolate text-lg mb-1" data-id="element-151">
                      Calculadora Automática de Precios
                    </h3>
                    <p className="text-chocolate-light leading-relaxed" data-id="element-152">
                      Deja de adivinar cuánto cobrar. Ingresa tus ingredientes y
                      la calculadora te dice tu{' '}
                      <span className="font-bold text-chocolate" data-id="element-153">
                        ganancia neta real
                      </span>{' '}
                      al instante. Nunca más vendas a pérdida.
                    </p>
                  </div>
                </div>

                {/* Item 2 */}
                <div className="bg-white rounded-2xl p-6 border border-chocolate/10 shadow-sm flex gap-5 items-start" data-id="element-154">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center shrink-0" data-id="element-155">
                    <MessageCircle className="w-6 h-6 text-green-600" data-id="element-156" />
                  </div>
                  <div data-id="element-157">
                    <h3 className="font-bold text-chocolate text-lg mb-1" data-id="element-158">
                      Scripts de Cierre en WhatsApp
                    </h3>
                    <p className="text-chocolate-light leading-relaxed" data-id="element-159">
                      Qué decir exactamente cuando te escriben "¿cuánto cuesta?"
                      para que te digan que{' '}
                      <span className="font-bold text-chocolate" data-id="element-160">SÍ</span>.
                      Mensajes probados, listos para copiar y pegar.
                    </p>
                  </div>
                </div>

                {/* Item 3 */}
                <div className="bg-white rounded-2xl p-6 border border-chocolate/10 shadow-sm flex gap-5 items-start" data-id="element-161">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0" data-id="element-162">
                    <Users className="w-6 h-6 text-amber-600" data-id="element-163" />
                  </div>
                  <div data-id="element-164">
                    <h3 className="font-bold text-chocolate text-lg mb-1" data-id="element-165">
                      Guía de Clientes Fieles
                    </h3>
                    <p className="text-chocolate-light leading-relaxed" data-id="element-166">
                      Convierte un pedido de $5 en{' '}
                      <span className="font-bold text-chocolate" data-id="element-167">
                        ingresos recurrentes
                      </span>
                      . Aprende a fidelizar clientes para que te compren semana
                      tras semana sin que tengas que rogarles.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Mockup Image */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.4
        }} className="mb-12" data-id="element-168">
              <div className="bg-white rounded-2xl overflow-hidden shadow-soft border border-gold/20" data-id="element-169">
                <div className="aspect-[16/9] bg-cream-dark" data-id="element-170">
                  <img src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?q=80&w=1600&auto=format&fit=crop" alt="Mano de mujer con móvil, calculadora, papel y lápiz en mesón de cocina" className="w-full h-full object-cover" data-id="element-171" />
                </div>
                <div className="p-4 text-center" data-id="element-172">
                  <p className="text-sm text-chocolate-light italic" data-id="element-173">
                    Vista previa: Calculadora de costos + Scripts de WhatsApp
                    listos para usar
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Pricing */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="text-center mb-8" data-id="element-174">
              <p className="text-lg text-chocolate-light mb-2" data-id="element-175">
                Valor individual del Kit:{' '}
                <span className="line-through decoration-rose-500 decoration-2 font-bold" data-id="element-176">
                  $10.36 USD
                </span>
              </p>
              <p className="text-chocolate-light mb-1 text-sm uppercase tracking-widest font-bold" data-id="element-177">
                Hoy con tu pack, llévalo por solo
              </p>
              <div className="flex items-center justify-center gap-2 text-chocolate mb-2" data-id="element-178">
                <span className="text-2xl font-bold" data-id="element-179">+$</span>
                <span className="text-6xl font-black tracking-tighter" data-id="element-180">
                  3.51
                </span>
                <span className="text-lg font-bold self-end mb-2" data-id="element-181">USD</span>
              </div>
              <span className="inline-block mb-3 bg-rose-600 text-white text-sm font-bold px-4 py-1 rounded-full" data-id="element-182">
                66% DE DESCUENTO
              </span>
              <p className="text-sm text-chocolate-light" data-id="element-183">
                Total con tu pack:{' '}
                <span className="font-bold text-chocolate" data-id="element-184">
                  $6.39 + $3.51 = $9.90 USD
                </span>{' '}
                (pago único)
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="flex flex-col items-center gap-5 mb-12" data-id="element-185">
              {/* Accept */}
              <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={onAccept} className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold text-lg md:text-xl py-5 px-8 rounded-full shadow-[0_8px_20px_-6px_rgba(217,108,117,0.6)] transition-colors duration-300 flex items-center justify-center gap-3" data-id="element-186">
                <span className="text-center leading-tight" data-id="element-187">
                  SÍ, añadir el Kit de Ventas y asegurar mi éxito por solo $3.51
                  adicionales
                </span>
                <ArrowRight className="w-6 h-6 shrink-0" data-id="element-188" />
              </motion.button>

              {/* Buy main offer only */}
              <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={onDecline} className="w-full bg-chocolate hover:bg-chocolate-light text-cream font-bold text-base md:text-lg py-4 px-8 rounded-full transition-colors duration-300 flex items-center justify-center gap-3" data-id="element-189">
                <span className="text-center leading-tight" data-id="element-190">
                  Continuar solo con el MEGA PACK 8 Ebooks por $6.39 USD
                </span>
                <ArrowRight className="w-5 h-5 shrink-0" data-id="element-191" />
              </motion.button>

              {/* Decline text */}
              <button onClick={onDecline} className="text-chocolate-light/50 hover:text-chocolate-light text-xs md:text-sm underline underline-offset-4 transition-colors py-2 max-w-md text-center leading-relaxed" data-id="element-192">
                No gracias, prefiero calcular mis costos y buscar clientes por
                mi cuenta.
              </button>
            </motion.div>

            {/* Trust */}
            <div className="flex items-center justify-center gap-2 text-sm text-chocolate-light/60" data-id="element-193">
              <ShieldCheck className="w-5 h-5 text-green-600" data-id="element-194" />
              <span data-id="element-195">
                Compra 100% segura · Acceso inmediato · Didactia Digital
              </span>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}